import { ArrowLeft } from "lucide-react";
import { useCallback, useRef, useState, type JSX, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

import { useCan } from "@/shared/auth";
import { PageHeader } from "@/shared/components/PageHeader";
import { cn } from "@/shared/lib/cn";
import { isFeatureEnabled } from "@/shared/lib/feature-flags";

import { usePromotion } from "../api/use-promotion";
import { ActivatePanel } from "../components/ActivatePanel";
import { SecondaryButton } from "../components/buttons";
import { PerformancePanel } from "../components/PerformancePanel";
import { PromotionBuilder } from "../components/PromotionBuilder";
import { PromotionStatusBadge } from "../components/PromotionStatusBadge";
import { SimulatePanel } from "../components/SimulatePanel";
import type { ActivationReason } from "../lib/activation-reasons";
import { formatPeriod } from "../lib/format-period";
import {
  hasPerformance,
  periodCaption,
  periodInfo,
  resolveTab,
  type PerformanceTab,
} from "../lib/performance";

/** Roving-tabindex order for ArrowLeft/ArrowRight/Home/End on the tab strip. */
const TAB_ORDER = ["setup", "performance"] as const satisfies readonly PerformanceTab[];

interface PromotionBuilderPageProps {
  promotionId: string;
  /** Routes (F4) inject navigation; tests/standalone omit it. */
  onBack?: () => void;
  /** `?tab=` from the route (spec §5.1). Omitted → the status default. */
  tab?: PerformanceTab;
  onTabChange?: (tab: PerformanceTab) => void;
}

/**
 * Builder composition (spec §5.4): the rule builder + a right-rail Simulation
 * panel + the activation control. Simulate and Activate both save the
 * builder's current edits first (`saveFirst`) so they act on what the user
 * is looking at, not the stale persisted promotion. The Simulate panel can
 * be hidden entirely via the `hide_promotion_simulate` ops kill-switch.
 */
export function PromotionBuilderPage({
  promotionId,
  onBack,
  tab,
  onTabChange,
}: PromotionBuilderPageProps): JSX.Element {
  const { t } = useTranslation(["promotion", "nav"]);
  const detail = usePromotion(promotionId);
  const status = detail.data?.status;
  const promotion = detail.data;
  const shownTab = resolveTab(status, tab);
  const caption = promotion
    ? periodCaption(promotion.status, periodInfo(promotion.start_at, promotion.end_at, new Date()))
    : null;
  const periodLine =
    promotion && caption
      ? `${formatPeriod(promotion.start_at, promotion.end_at)} · ${t(`promotion:${caption.key}`, caption.values)}`
      : null;

  // Standard ARIA tabs keyboard pattern: Left/Right roves with wraparound, Home/End
  // jump to the ends. Moves BOTH selection (via onTabChange) and DOM focus, since the
  // target tab is always mounted (both tabs render unconditionally in the strip).
  // Lives on each tab button (not the tablist div) — jsx-a11y/interactive-supports-focus
  // flags an interactive-role container with a key handler but no focus of its own; the
  // ARIA APG pattern puts focus (roving tabindex) on the tabs themselves anyway.
  const handleTabsKeyDown = useCallback(
    (event: KeyboardEvent<HTMLButtonElement>) => {
      const currentIndex = TAB_ORDER.indexOf(shownTab);
      let nextIndex: number;
      if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % TAB_ORDER.length;
      else if (event.key === "ArrowLeft")
        nextIndex = (currentIndex - 1 + TAB_ORDER.length) % TAB_ORDER.length;
      else if (event.key === "Home") nextIndex = 0;
      else if (event.key === "End") nextIndex = TAB_ORDER.length - 1;
      else return;

      event.preventDefault();
      const next = TAB_ORDER[nextIndex] ?? shownTab;
      onTabChange?.(next);
      document.getElementById(`promotion-tab-${next}`)?.focus();
    },
    [shownTab, onTabChange],
  );
  // Activation is a draft-only action (draft→active). Only surface it once the
  // promotion has loaded and is genuinely a draft — never while status is
  // unknown — so we don't flash an illegal control for active/finished promos.
  const isDraft = status === "draft";
  const canManage = useCan("promotions:manage");

  // Activate/Simulate must act on the user's CURRENT builder edits, not the stale
  // persisted promotion. The builder registers its imperative save here; the
  // panels call `saveFirst` before they hit the server, so precheck/simulate see
  // the just-saved rules. A blocked/errored save resolves `false` and aborts the
  // action (the builder already toasted why).
  const saveRef = useRef<(() => Promise<boolean>) | null>(null);
  const saveFirst = useCallback(async () => (saveRef.current ? saveRef.current() : true), []);

  // The builder reports {groupId, disabled} whenever this promotion belongs to a
  // linked draft group at all (onRegisterActivateGroup, Task 11), regardless of
  // remaining member count; ActivatePanel derives the member count itself from
  // `groupId` via its own useLinkedGroup call and decides whether to switch to
  // "Activate for N channels". A stable (useCallback, no deps) + bail-out setter
  // keeps this from looping: the builder's registration effect depends on this
  // prop's identity, so an unstable inline fn would re-fire on every page render.
  const [groupActivate, setGroupActivate] = useState<{ groupId: string; disabled: boolean } | null>(
    null,
  );
  const handleRegisterActivateGroup = useCallback((groupId: string, disabled: boolean) => {
    setGroupActivate((prev) =>
      prev?.groupId === groupId && prev.disabled === disabled ? prev : { groupId, disabled },
    );
  }, []);

  // Reasons from a blocked precheck (ActivatePanel's onBlocked), threaded down
  // into the builder so it can paint the offending field instead of the bare
  // focus ring QA flagged as the whole "highlight" (TC59).
  const [precheckReasons, setPrecheckReasons] = useState<ActivationReason[] | null>(null);

  return (
    <div data-testid="promotion-builder-page">
      <PageHeader
        breadcrumb={
          <>
            <span>{t("nav:group.promotion")}</span>
            <span className="text-[var(--premium-border-strong)]">/</span>
            <span className="text-[var(--text-secondary)]">{t("promotion:detail.title")}</span>
          </>
        }
        title={detail.data?.name ?? t("promotion:detail.title")}
        {...(periodLine ? { subtitle: periodLine } : {})}
        actions={
          <div className="flex items-center gap-2">
            {status && <PromotionStatusBadge status={status} dot={status === "active"} />}
            {onBack && (
              <SecondaryButton data-testid="builder-back" onClick={onBack}>
                <ArrowLeft className="h-4 w-4" aria-hidden />
                {t("promotion:detail.back")}
              </SecondaryButton>
            )}
          </div>
        }
      />

      {hasPerformance(status) && (
        <div
          role="tablist"
          aria-label={t("promotion:performance.tabs_label")}
          className="mb-5 flex items-center gap-1.5 border-b border-[var(--premium-border)]"
        >
          {TAB_ORDER.map((key) => {
            const active = shownTab === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                id={`promotion-tab-${key}`}
                aria-selected={active}
                aria-controls={`promotion-tabpanel-${key}`}
                tabIndex={active ? 0 : -1}
                data-testid={`promotion-tab-${key}`}
                onClick={() => onTabChange?.(key)}
                onKeyDown={handleTabsKeyDown}
                className={cn(
                  "relative px-[18px] pb-3 pt-1 text-[13.5px] font-semibold transition-colors",
                  active
                    ? "text-[var(--brand-700)]"
                    : "text-[var(--text-subtle)] hover:text-[var(--text-primary)]",
                )}
              >
                {t(`promotion:performance.tab_${key}`)}
                {active && (
                  <span className="absolute -bottom-px left-0 right-0 h-[2.5px] rounded-[3px] bg-[var(--brand-500)]" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Setup stays mounted while hidden so unsaved builder edits survive a tab switch.
          `hidden` sits on a plain wrapper: on the grid itself `.grid` would override it.
          tabpanel/labelledby only apply once the tab strip actually exists (draft has
          neither a tablist nor anything to point `aria-labelledby` at). */}
      <div
        data-testid="promotion-setup-panel"
        hidden={shownTab !== "setup"}
        {...(hasPerformance(status)
          ? {
              role: "tabpanel" as const,
              id: "promotion-tabpanel-setup",
              "aria-labelledby": "promotion-tab-setup",
              tabIndex: 0,
            }
          : {})}
      >
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div>
            <PromotionBuilder
              promotionId={promotionId}
              precheckReasons={precheckReasons}
              onRegisterSave={(fn) => {
                saveRef.current = fn;
              }}
              onRegisterActivateGroup={handleRegisterActivateGroup}
              readOnly={!canManage}
            />
          </div>
          <aside className="space-y-6 lg:sticky lg:top-20 lg:self-start">
            {isDraft && canManage && (
              <section className="rounded-2xl border border-[var(--premium-border)] bg-white p-4">
                <ActivatePanel
                  promotionId={promotionId}
                  onBeforeActivate={saveFirst}
                  // Spread into a fresh array: TanStack Query's structural sharing
                  // returns the SAME `reasons` reference on a refetch that produces a
                  // deeply-equal result, which would make React bail out of re-running
                  // the highlight effect below on a repeat blocked activation (TC59).
                  onBlocked={(r) => setPrecheckReasons([...r])}
                  onActivated={() => setPrecheckReasons(null)}
                  groupId={groupActivate?.groupId ?? null}
                  groupDisabled={groupActivate?.disabled ?? false}
                />
              </section>
            )}
            {!isFeatureEnabled("hide_promotion_simulate") && (
              <SimulatePanel promotionId={promotionId} onBeforeRun={saveFirst} />
            )}
          </aside>
        </div>
      </div>

      {shownTab === "performance" && promotion && (
        <div
          data-testid="promotion-performance-panel"
          role="tabpanel"
          id="promotion-tabpanel-performance"
          aria-labelledby="promotion-tab-performance"
          tabIndex={0}
        >
          <PerformancePanel promotion={promotion} />
        </div>
      )}
    </div>
  );
}
